import { BRAND, FOUNDER } from "@/content/thoratech";
import { Quote, Shield, MapPin, Calendar, ArrowUpRight } from "lucide-react";

const SUCCESS_IMG =
  "https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODA5NDY5Nzh8MA&ixlib=rb-4.1.0&q=85";

const FOUNDER_IMG = "/brand/founder.png";

export function SuccessStory() {
  return (
    <section
      id="success-story"
      data-testid="success-story-section"
      className="relative bg-[#01080E] text-white py-24 md:py-32 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="font-mono-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-[#7BDCB5]">
                Proof · Success story
              </span>
            </div>

            <Quote size={42} className="text-[#2AC0A5] mb-6" />
            <p className="font-display text-2xl md:text-4xl leading-tight font-semibold text-white max-w-3xl">
              We run a live managed-operations partnership with a leading industrial and
              power-equipment manufacturer, delivering governed customer operations under
              <span className="text-[#2AC0A5]"> SLA-driven performance.</span>
            </p>

            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-2xl">
              {[
                { k: "Engagement", v: "Managed Ops" },
                { k: "Governance", v: "SLA-Driven" },
                { k: "Industry", v: "Power Equipment" },
                { k: "Status", v: "Live engagement" },
              ].map((b, i) => (
                <div key={i} className="bg-[#01080E] p-5">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
                    {b.k}
                  </div>
                  <div className="mt-2 font-display text-base md:text-lg font-semibold text-white">
                    {b.v}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-8 text-sm text-white/55 max-w-xl">
              Anonymous by design. We don&apos;t name clients in marketing. A detailed case study is
              available on request under NDA.
            </p>

            <a
              href="#contact"
              data-testid="success-story-cta"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#contact");
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 72;
                  window.scrollTo({ top, behavior: "smooth" });
                }
              }}
              className="mt-8 inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors"
            >
              Request case study
              <ArrowUpRight size={18} />
            </a>
          </div>

          <div className="lg:col-span-5">
            <div className="relative border border-white/10">
              <img
                src={SUCCESS_IMG}
                alt="Industrial manufacturing context, client environment"
                className="w-full h-[420px] object-cover grayscale contrast-110"
                loading="lazy"
              />
              <div aria-hidden className="absolute inset-0 bg-[#01080E]/30" />
              <div className="absolute left-0 right-0 bottom-0 p-5 flex items-center justify-between border-t border-white/15 bg-[#01080E]/70 backdrop-blur">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                  Client environment
                </span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/60">
                  Identity withheld
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-white border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={FOUNDER_IMG}
                alt={`${FOUNDER.name}, ${FOUNDER.title}, Thoratech`}
                className="w-full h-[520px] object-cover object-top"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#2AC0A5] text-[#01080E] px-5 py-3 font-display font-semibold">
                Founder-led
              </div>
            </div>
            <div className="mt-7">
              <div className="font-display text-xl font-semibold text-[#01080E]">{FOUNDER.name}</div>
              <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#01080E]/55 mt-1">
                {FOUNDER.title}
              </div>
              <a
                href={BRAND.contact.linkedinFounder}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-founder-linkedin"
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#01080E] hover:text-[#2AC0A5] transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Connect with Harshal on LinkedIn
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                About & Founder
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
              Built by operators.
              <br />
              <span className="text-[#2AC0A5]">Run like an operation.</span>
            </h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
              <div className="bg-white p-5">
                <Calendar size={18} className="text-[#2AC0A5] mb-2" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                  Incorporated
                </div>
                <div className="mt-1 font-display text-lg font-semibold">{BRAND.yearIncorporated}</div>
              </div>
              <div className="bg-white p-5">
                <MapPin size={18} className="text-[#2AC0A5] mb-2" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                  Headquarters
                </div>
                <div className="mt-1 font-display text-lg font-semibold">Pune, India</div>
              </div>
              <div className="bg-white p-5">
                <Shield size={18} className="text-[#2AC0A5] mb-2" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                  Status
                </div>
                <div className="mt-1 font-display text-lg font-semibold">Revenue-generating</div>
              </div>
            </div>

            <p className="mt-8 text-lg leading-relaxed text-[#01080E]/80">
              <strong className="text-[#01080E]">Thoratech Innovations Pvt Ltd.</strong> Headquartered
              in Pune, India. Founder-led and revenue-generating.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-[#01080E]/80">
              {FOUNDER.bio}
            </p>

            <div className="mt-10 border-t border-[#01080E]/10 pt-6">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55 mb-4">
                Industries served across the founder&apos;s career
              </div>
              <div className="flex flex-wrap gap-2">
                {FOUNDER.industriesServed.map((c) => (
                  <span
                    key={c}
                    data-testid={`founder-industry-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    className="inline-flex items-center text-xs font-medium text-[#01080E]/75 border border-[#01080E]/15 px-3 py-1.5 hover:border-[#2AC0A5] hover:text-[#01080E] transition-colors"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs text-[#01080E]/50 italic">{FOUNDER.industriesDisclaimer}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function EngagementModel() {
  return (
    <section
      id="engagement"
      data-testid="engagement-section"
      className="relative py-24 md:py-32 bg-[#FAFAF8] border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                How we work with you
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
              Outcome-based engagements.
              <br />
              Governed by contract.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-[#01080E]/80">
              You pay for results and accountability, not just hours or seats. Every engagement
              begins with a <strong>free process audit</strong>: we map your current customer
              operations, identify the gaps and define the outcomes we&apos;ll own.
            </p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
              {[
                { n: "01", t: "Scope definition", d: "Map pain points, define processes and the KPIs that matter." },
                { n: "02", t: "Pilot proposal", d: "Team size, KPIs, SLAs and commercial terms, in writing." },
                { n: "03", t: "Onboard & go-live", d: "Train, certify and calibrate the team, then start operations." },
                { n: "04", t: "Govern & scale", d: "Weekly reviews, monthly MIS and structured expansion." },
              ].map((s) => (
                <div key={s.n} data-testid={`engagement-step-${s.n}`} className="bg-white p-6">
                  <div className="font-mono-tech text-xs font-semibold tracking-[0.2em] text-[#2AC0A5]">
                    / {s.n}
                  </div>
                  <div className="mt-3 font-display text-lg md:text-xl font-semibold text-[#01080E]">{s.t}</div>
                  <div className="mt-2 text-sm text-[#01080E]/70 leading-relaxed">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProgressKode() {
  return (
    <section
      id="progress-kode"
      data-testid="progress-kode-section"
      className="relative py-16 md:py-20 bg-white border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-[#01080E] text-white p-8 md:p-12 relative overflow-hidden">
          <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-30" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-3 flex md:justify-start">
              <img
                src="/brand/progresskode_dark.png"
                alt="progressKODE logo"
                className="h-20 md:h-24 w-auto"
              />
            </div>
            <div className="md:col-span-6">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                A Thoratech company
              </span>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white leading-snug">
                Need software, not just operations?{" "}
                <span className="text-[#2AC0A5]">progressKODE</span> builds configurable software
               and automation.
              </h3>
            </div>
            <div className="md:col-span-3 md:text-right">
              <a
                href="https://progresskode.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="progress-kode-link"
                className="inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors"
              >
                Visit progressKODE
                <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
